import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import axios from "api/axios";
import { setTwoFactorRequired, setUser } from "state";
import { api } from "state/api";

/**
 * Non-dismissible SMS 2FA gate. Blocks all app interaction until verified.
 * Re-challenged on every login (cookie cleared) and every 2 hours (cookie TTL).
 */
const TwoFactorGate = ({ open }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [phones, setPhones] = useState([]);
  const [phoneId, setPhoneId] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("load"); // load | pick | code
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    const load = async () => {
      setError("");
      setStep("load");
      try {
        const { data } = await axios.get("/auth/2fa/status");
        if (cancelled) return;
        if (!data.required) {
          dispatch(setTwoFactorRequired(false));
          return;
        }
        setPhones(data.phones || []);
        if (data.phones?.length === 1) {
          setPhoneId(data.phones[0].id);
        }
        setStep("pick");
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || "Could not load two-factor status");
          setStep("pick");
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, dispatch]);

  const handleSend = async () => {
    if (!phoneId) {
      setError("Select a phone number");
      return;
    }
    setSending(true);
    setError("");
    setInfo("");
    try {
      const { data } = await axios.post("/auth/2fa/send", { phoneId });
      const dest = data.phone?.masked || "your phone";
      if (data.smsChannel === "console") {
        setInfo(`Twilio not configured — code logged on the server console for ${dest}`);
      } else {
        setInfo(`Code sent to ${dest}`);
      }
      setStep("code");
      setCode("");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send code");
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setError("");
    try {
      await axios.post("/auth/2fa/verify", { code: code.trim() });
      const me = await axios.get("/auth/me");
      dispatch(setUser(me.data));
      dispatch(setTwoFactorRequired(false));
      dispatch(api.util.resetApiState());
    } catch (err) {
      setError(err?.response?.data?.message || "Invalid code");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(8, 12, 20, 0.72)",
          backdropFilter: "blur(10px)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 0.5,
          backgroundImage: "none",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75, py: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LockOutlined color="primary" />
            <Typography fontWeight={750} fontSize="1.05rem">
              Two-factor verification
            </Typography>
          </Box>
          <Typography fontSize="0.85rem" color="text.secondary">
            Enter the SMS code to continue. This check is required after every login and again every 2 hours.
            You cannot browse the site until verification succeeds.
          </Typography>

          {step === "load" && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {step !== "load" && (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>Send code to</InputLabel>
                <Select
                  label="Send code to"
                  value={phoneId}
                  onChange={(e) => setPhoneId(e.target.value)}
                  disabled={sending || verifying}
                >
                  {phones.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {(p.label ? `${p.label} — ` : "") + p.masked}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {step === "pick" && (
                <Button
                  variant="contained"
                  onClick={handleSend}
                  disabled={sending || !phoneId}
                >
                  {sending ? "Sending…" : "Send SMS code"}
                </Button>
              )}

              {step === "code" && (
                <>
                  <TextField
                    label="6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                    disabled={verifying}
                    fullWidth
                    size="small"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && code.length === 6) handleVerify();
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleVerify}
                      disabled={verifying || code.length !== 6}
                      fullWidth
                    >
                      {verifying ? "Verifying…" : "Verify & continue"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleSend}
                      disabled={sending}
                    >
                      Resend
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}

          {info && (
            <Typography fontSize="0.8rem" color="success.main">
              {info}
            </Typography>
          )}
          {error && (
            <Typography fontSize="0.8rem" color="error.main">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorGate;
